import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  RiCloseLine,
  RiBuildingLine,
  RiMapPinLine,
  RiArrowDownSLine,
  RiCheckLine,
} from '@remixicon/react';
import { toast } from 'sonner';
import PhoneInput from '@components/forms/PhoneInput';

const POC_TITLES = [
  'Chief Executive Officer',
  'Chief Operating Officer',
  'Chief Financial Officer',
  'President',
  'Vice President',
  'Director of Operations',
  'Owner',
  'Manager',
];

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const EditOrganizationDrawer = ({ open, onClose, initialValues = {}, onSubmit }) => {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [phone, setPhone] = useState('');
  const [pocTitle, setPocTitle] = useState('');
  const [pocOpen, setPocOpen] = useState(false);
  const [soleProprietorship, setSoleProprietorship] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initialValues.name || '');
      setAddress(initialValues.address || '');
      setCountryCode(initialValues.countryCode || '+1');
      setPhone(initialValues.phone || '');
      setPocTitle(initialValues.pocTitle || '');
      setSoleProprietorship(!!initialValues.soleProprietorship);
      setPocOpen(false);
      setSaving(false);
    }
  }, [
    open,
    initialValues.name,
    initialValues.address,
    initialValues.countryCode,
    initialValues.phone,
    initialValues.pocTitle,
    initialValues.soleProprietorship,
  ]);

  const canSubmit = name.trim() && address.trim() && phone.trim() && pocTitle && !saving;

  const handleSave = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const payload = { name, address, countryCode, phone, pocTitle, soleProprietorship };
      await onSubmit?.(payload);
      onClose();
      toast.success('Organization updated successfully', { position: 'bottom-right' });
    } catch (err) {
      toast.error(err?.message || 'Failed to update organization', { position: 'bottom-right' });
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <>
      <div
        className={`fixed inset-0 z-[9998] bg-black/40 transition-opacity duration-300 ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed top-0 right-0 bottom-0 z-[9999] w-full sm:w-[560px] lg:w-[640px] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-4">
          <h3 className="text-lg font-semibold text-primary-700">Edit organization details</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-4">
          <Field label="Brokerage Company Name" required>
            <div className="relative flex items-center">
              <RiBuildingLine className="absolute left-3 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </Field>

          <Field label="Address" required>
            <div className="relative flex items-center">
              <RiMapPinLine className="absolute left-3 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </Field>

          <PhoneInput
            countryCode={countryCode}
            phone={phone}
            onCountryCodeChange={setCountryCode}
            onPhoneChange={setPhone}
            label="Phone"
            required
          />

          <Field label="Point-of-Contact Title" required>
            <div className="relative">
              <button
                type="button"
                onClick={() => setPocOpen((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-white hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <span className={pocTitle ? 'text-gray-900' : 'text-gray-400'}>
                  {pocTitle || 'Select title'}
                </span>
                <RiArrowDownSLine className="w-4 h-4 text-gray-400" />
              </button>
              {pocOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto z-10">
                  {POC_TITLES.map((title) => (
                    <button
                      key={title}
                      type="button"
                      onClick={() => {
                        setPocTitle(title);
                        setPocOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                        pocTitle === title ? 'text-primary-700 font-medium' : 'text-gray-700'
                      }`}
                    >
                      {title}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </Field>

          <label className="flex items-center gap-2 cursor-pointer select-none pt-1">
            <input
              type="checkbox"
              checked={soleProprietorship}
              onChange={(e) => setSoleProprietorship(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">Sole Proprietorship</span>
          </label>
        </div>

        <div className="px-6 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSubmit}
            className={`w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-medium transition-colors ${
              canSubmit
                ? 'bg-primary-700 text-white hover:bg-primary-800'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            <RiCheckLine className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

export default EditOrganizationDrawer;
